/**
 * CONTRACT: per-tile terrain byte encoding (shared by map builder, sim, renderer).
 *
 *   bit 7 (0x80)  LAND  – 1 = land, 0 = water
 *   bit 6 (0x40)  SHORE – land tile with a 4-neighbour water tile, or water tile with a 4-neighbour land tile
 *   bit 5 (0x20)  LAKE  – water only: 1 = lake (enclosed water), 0 = ocean
 *   bits 0..4     MAGNITUDE 0..31 – land: elevation; water: depth (≈ distance to shore, capped at 31)
 *
 * Land terrain classes by magnitude: 0..9 plains, 10..19 highland, 20..31 mountain.
 *
 * Binary map file (.bin), little endian:
 *   offset 0  4 bytes  magic "CQM1"
 *   offset 4  u16      version (1)
 *   offset 6  u16      width
 *   offset 8  u16      height
 *   offset 10 u16      reserved (0)
 *   offset 12 width*height bytes terrain (row major, y * width + x)
 * Accompanied by <id>.json containing MapMeta (src/shared/protocol.ts).
 */
import { Climate, ResourceType, TerrainType } from "../types";

export const LAND_BIT = 0x80;
export const SHORE_BIT = 0x40;
export const LAKE_BIT = 0x20;
export const MAGNITUDE_MASK = 0x1f;

export const PLAINS_MAX_MAGNITUDE = 9;
export const HIGHLAND_MAX_MAGNITUDE = 19;

export const MAP_MAGIC = "CQM1";
export const MAP_VERSION = 1;
export const MAP_HEADER_BYTES = 12;

export function isLandByte(b: number): boolean {
  return (b & LAND_BIT) !== 0;
}

export function isShoreByte(b: number): boolean {
  return (b & SHORE_BIT) !== 0;
}

export function magnitudeOf(b: number): number {
  return b & MAGNITUDE_MASK;
}

export function terrainTypeOf(b: number): TerrainType {
  if ((b & LAND_BIT) === 0) return (b & LAKE_BIT) !== 0 ? TerrainType.Lake : TerrainType.Ocean;
  const m = b & MAGNITUDE_MASK;
  if (m <= PLAINS_MAX_MAGNITUDE) return TerrainType.Plains;
  if (m <= HIGHLAND_MAX_MAGNITUDE) return TerrainType.Highland;
  return TerrainType.Mountain;
}

export function encodeTerrainByte(land: boolean, magnitude: number, lake = false, shore = false): number {
  let b = Math.max(0, Math.min(31, magnitude | 0));
  if (land) b |= LAND_BIT;
  else if (lake) b |= LAKE_BIT;
  if (shore) b |= SHORE_BIT;
  return b;
}

/**
 * Parse a .bin map file (v1: terrain only; v2: terrain + biome layer). Throws on bad magic / size.
 * `biome` is undefined for v1 maps; derive one with a deterministic generator when needed.
 */
export function decodeMapBinary(buf: ArrayBuffer): { width: number; height: number; terrain: Uint8Array; biome?: Uint8Array } {
  const view = new DataView(buf);
  const magic = String.fromCharCode(view.getUint8(0), view.getUint8(1), view.getUint8(2), view.getUint8(3));
  if (magic !== MAP_MAGIC) throw new Error(`Bad map magic: ${magic}`);
  const version = view.getUint16(4, true);
  if (version !== MAP_VERSION && version !== MAP_VERSION_BIOME) throw new Error(`Unsupported map version ${version}`);
  const width = view.getUint16(6, true);
  const height = view.getUint16(8, true);
  const n = width * height;
  const expected = MAP_HEADER_BYTES + n * (version === MAP_VERSION_BIOME ? 2 : 1);
  if (buf.byteLength < expected) throw new Error(`Map truncated: ${buf.byteLength} < ${expected}`);
  const terrain = new Uint8Array(buf.slice(MAP_HEADER_BYTES, MAP_HEADER_BYTES + n));
  if (version === MAP_VERSION) return { width, height, terrain };
  const biome = new Uint8Array(buf.slice(MAP_HEADER_BYTES + n, MAP_HEADER_BYTES + 2 * n));
  return { width, height, terrain, biome };
}

/** Build a .bin map file: v1 from terrain bytes, or v2 when a biome layer is given. */
export function encodeMapBinary(width: number, height: number, terrain: Uint8Array, biome?: Uint8Array): Uint8Array {
  const n = width * height;
  const out = new Uint8Array(MAP_HEADER_BYTES + n * (biome ? 2 : 1));
  const view = new DataView(out.buffer);
  for (let i = 0; i < 4; i++) view.setUint8(i, MAP_MAGIC.charCodeAt(i));
  view.setUint16(4, biome ? MAP_VERSION_BIOME : MAP_VERSION, true);
  view.setUint16(6, width, true);
  view.setUint16(8, height, true);
  view.setUint16(10, 0, true);
  out.set(terrain.subarray(0, n), MAP_HEADER_BYTES);
  if (biome) out.set(biome.subarray(0, n), MAP_HEADER_BYTES + n);
  return out;
}

// ---------------------------------------------------------------------------
// Wave 4: biome byte (one per tile, map format v2 appends width*height of them)
//
//   bits 0..2  CLIMATE   (Climate enum in src/core/types.ts; on water: cold sea = Tundra)
//   bits 3..5  RESOURCE  (ResourceType enum; 0 = none; land only)
//   bit  6     RIVER     (land tile crossed by a river)
//   bit  7     water: FISH (a fish shoal, wave 4 stage I economy2: food for the owner of the
//              nearby coast); land: 0 in map files (CONTAMINATED at runtime, map/mutation.ts)
// ---------------------------------------------------------------------------

export const MAP_VERSION_BIOME = 2;
export const CLIMATE_MASK = 0x07;
export const RESOURCE_SHIFT = 3;
export const RESOURCE_MASK = 0x38;
export const RIVER_BIT = 0x40;
/**
 * Wave 4 stage I (economy2): biome bit 7 on a WATER tile = a fish shoal (map data, generated with
 * the biome, src/core/map/biome/fish.ts). On land the same bit is the runtime contamination flag
 * (map/mutation.ts CONTAMINATED_BIT): always check LAND_BIT before reading either meaning.
 */
export const FISH_BIT = 0x80;

/** A water tile with a fish shoal (terrain byte + biome byte). */
export function isFishTile(terrainByte: number, biomeByte: number): boolean {
  return (terrainByte & LAND_BIT) === 0 && (biomeByte & FISH_BIT) !== 0;
}

export function climateOf(b: number): Climate {
  return (b & CLIMATE_MASK) as Climate;
}

export function resourceOf(b: number): ResourceType {
  return ((b & RESOURCE_MASK) >> RESOURCE_SHIFT) as ResourceType;
}

export function hasRiverByte(b: number): boolean {
  return (b & RIVER_BIT) !== 0;
}

export function encodeBiomeByte(climate: Climate, resource: ResourceType = ResourceType.None, river = false): number {
  let b = (climate & CLIMATE_MASK) | ((resource << RESOURCE_SHIFT) & RESOURCE_MASK);
  if (river) b |= RIVER_BIT;
  return b;
}

/** Recompute SHORE bits in place (4-neighbourhood). */
export function recomputeShore(width: number, height: number, terrain: Uint8Array): void {
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = y * width + x;
      const land = (terrain[i] & LAND_BIT) !== 0;
      let shore = false;
      if (x > 0 && ((terrain[i - 1] & LAND_BIT) !== 0) !== land) shore = true;
      else if (x < width - 1 && ((terrain[i + 1] & LAND_BIT) !== 0) !== land) shore = true;
      else if (y > 0 && ((terrain[i - width] & LAND_BIT) !== 0) !== land) shore = true;
      else if (y < height - 1 && ((terrain[i + width] & LAND_BIT) !== 0) !== land) shore = true;
      terrain[i] = shore ? terrain[i] | SHORE_BIT : terrain[i] & ~SHORE_BIT;
    }
  }
}
